import io from "socket.io-client";
import discovery from "../discovery";
import { getAccessToken, getClientCompanyId } from "@wkt/lib/auth-context";
import { useCordovaBridge } from "./useCordovaBridge";

type ChannelEvent = {
  message_type?: string;
  message_content?: string;
  service?: string;
  user_id?: string;
  server_timestamp?: number;
};

type ChannelPayload = {
  company_id?: number | string;
  payload?: string;
};

let socket: any = null;
let subscribedChannelName: string | null = null;
let subscribedToken: string | null = null;
let boundMessageHandler: ((event: ChannelEvent) => void) | null = null;
let pendingConnectTimer: number | null = null;

function clearPendingConnect(): void {
  if (pendingConnectTimer == null || typeof window === "undefined") {
    pendingConnectTimer = null;
    return;
  }

  window.clearTimeout(pendingConnectTimer);
  pendingConnectTimer = null;
}

function scheduleConnectRetry(username: string): void {
  if (typeof window === "undefined" || pendingConnectTimer != null) {
    return;
  }

  pendingConnectTimer = window.setTimeout(() => {
    pendingConnectTimer = null;
    useWebSocket().connect(username);
  }, 1000);
}

function safeParseMessageContent(messageContent?: string): ChannelPayload {
  if (!messageContent) {
    return {};
  }

  try {
    return JSON.parse(messageContent) as ChannelPayload;
  } catch (error) {
    console.warn("Failed to parse websocket message_content", error);
    return {};
  }
}

function getActiveCompanyId(): string | null {
  return getClientCompanyId();
}

function shouldProcessEvent(payload: ChannelPayload): boolean {
  const activeCompanyId = getActiveCompanyId();
  if (!payload.company_id || !activeCompanyId) {
    return true;
  }

  return String(payload.company_id) === String(activeCompanyId);
}

function buildPushEnvelope(event: ChannelEvent, payload: ChannelPayload): string {
  const isProfilePictureUpdate = event.message_type === "profile-picture-update";

  return JSON.stringify({
    data: {
      type: isProfilePictureUpdate ? "profile-picture-update" : "sync",
      payload: payload.payload || event.message_type || "",
    },
  });
}

function emitAsNativePush(event: ChannelEvent): void {
  const payload = safeParseMessageContent(event.message_content);
  if (!shouldProcessEvent(payload)) {
    return;
  }

  const pushEnvelope = buildPushEnvelope(event, payload);
  const cordovaBridge = useCordovaBridge();
  cordovaBridge.emitNativeDeviceEvent(`NE_PUSH:${pushEnvelope}`);
}

function triggerReconnectRefresh(): void {
  const cordovaBridge = useCordovaBridge();
  cordovaBridge.emitNativeDeviceEvent("NE_AWAKE:301");
}

export function useWebSocket() {
  function connect(username?: string | null): void {
    const normalizedUsername =
      typeof username === "string" ? username.trim() : "";
    if (!normalizedUsername) {
      return;
    }

    const channelUrl = discovery.socketio_channel_url;
    const environment = discovery.tweem_environment;
    if (!channelUrl || !environment) {
      console.warn("socketio-channel discovery settings are missing");
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      scheduleConnectRetry(normalizedUsername);
      return;
    }

    const channelName = `tweem-${environment}-${normalizedUsername}`;

    if (
      socket &&
      subscribedChannelName === channelName &&
      subscribedToken === accessToken
    ) {
      return;
    }

    clearPendingConnect();
    disconnect();

    socket = io(channelUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      query: {
        token: accessToken,
        targetApp: "worktime-client",
        targetEnv: environment,
      },
    });
    subscribedChannelName = channelName;
    subscribedToken = accessToken;
    boundMessageHandler = (event: ChannelEvent) => emitAsNativePush(event);

    socket.on("connect", () => {
      if (subscribedChannelName) {
        socket?.emit("join-channel", subscribedChannelName);
      }
    });

    socket.on("reconnect", () => {
      if (subscribedChannelName) {
        socket?.emit("join-channel", subscribedChannelName);
      }
      triggerReconnectRefresh();
    });

    socket.on(channelName, boundMessageHandler);
  }

  function disconnect(): void {
    if (!socket) {
      subscribedChannelName = null;
      subscribedToken = null;
      boundMessageHandler = null;
      clearPendingConnect();
      return;
    }

    if (subscribedChannelName) {
      if (boundMessageHandler) {
        socket.off(subscribedChannelName, boundMessageHandler);
      }
      socket.emit("leave-channel", subscribedChannelName);
    }

    socket.disconnect();
    socket = null;
    subscribedChannelName = null;
    subscribedToken = null;
    boundMessageHandler = null;
    clearPendingConnect();
  }

  return { connect, disconnect };
}
